// pages/public/PaymentPage.jsx
// Payment page with real PayPal integration
// Shows PayPal button when connected
// Shows setup message when not connected

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAssessment } from '../../context/AssessmentContext';
import axios from 'axios';

const PaymentPage = () => {
  const navigate = useNavigate();
  const { user, sessionToken, setPaymentStatus } = useAssessment();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchConfig();
  }, []);

  // Load PayPal SDK when config is ready
  useEffect(() => {
    if (config?.paypalConnected && config?.paypalClientId) {
      loadPayPalSDK(config.paypalClientId);
    }
  }, [config]);

  const fetchConfig = async () => {
    try {
      const response = await axios.get('/payment/config');
      if (response.data.success) {
        setConfig(response.data.data);
      }
    } catch (err) {
      setError('Could not load payment configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadPayPalSDK = (clientId) => {
    // Remove existing PayPal script if any
    const existingScript = document.getElementById('paypal-sdk');
    if (existingScript) existingScript.remove();

    const script = document.createElement('script');
    script.id = 'paypal-sdk';
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD`;
    script.onload = () => renderPayPalButton();
    document.body.appendChild(script);
  };

  const renderPayPalButton = () => {
    if (!window.paypal) return;

    window.paypal.Buttons({
      style: {
        layout: 'vertical',
        color: 'gold',
        shape: 'rect',
        label: 'pay',
        height: 48,
      },

      createOrder: async () => {
        try {
          const response = await axios.post('/payment/create-order', {
            userId: user?.id,
            sessionToken,
          });
          return response.data.data.orderId;
        } catch (err) {
          setError('Could not create payment. Please try again.');
          throw err;
        }
      },

      onApprove: async (data) => {
        setPaying(true);
        try {
          const response = await axios.post(`/payment/capture/${data.orderID}`, {
            userId: user?.id,
            sessionToken,
          });

          if (response.data.success) {
            setPaymentStatus('paid');
            navigate('/assessment');
          }
        } catch (err) {
          setError('Payment capture failed. Please contact support.');
        } finally {
          setPaying(false);
        }
      },

      onError: (err) => {
        setError('Payment failed. Please try again.');
        console.error('PayPal error:', err);
      },

      onCancel: () => {
        setError('Payment was cancelled. You can try again when ready.');
      },
    }).render('#paypal-button-container');
  };

  if (loading) {
    return (
      <div style={styles.loadingPage}>
        <div style={styles.spinner} />
        <p style={styles.loadingText}>Loading payment options...</p>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={styles.logo}>
          <span style={styles.logoExec}>EXECUTIVE</span>
          <span style={styles.logoSpring}>Springboard</span>
        </div>
      </div>

      <div style={styles.container}>
        <div style={styles.progressWrap}>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: '66%' }} />
          </div>
          <span style={styles.progressText}>Step 2 of 3</span>
        </div>

        <div style={styles.card}>
          <h1 style={styles.title}>Complete Your Purchase</h1>
          <p style={styles.subtitle}>
            Hi {user?.firstName}! You are one step away from your leadership assessment.
          </p>

          {/* Price Box */}
          <div style={styles.priceBox}>
            <div style={styles.priceLabel}>Leadership Assessment Report</div>
            <div style={styles.priceAmount}>${config?.assessmentPrice || 149}</div>
            <div style={styles.priceNote}>One time payment. Includes free snapshot and detailed report.</div>
          </div>

          {/* What is included */}
          <div style={styles.includesBox}>
            <div style={styles.includesTitle}>What is included:</div>
            {[
              'Free leadership snapshot delivered instantly',
              'Comprehensive report with dimension analysis',
              '90 day development roadmap',
              '30 minute one-on-one analytic consultation with Steve Moss',
            ].map((item, i) => (
              <div key={i} style={styles.includeItem}>
                <span style={styles.includeCheck}>✓</span>
                <span>{item}</span>
              </div>
            ))}
          </div>

          {error && <div style={styles.errorBox}>{error}</div>}

          {paying && (
            <div style={styles.payingBox}>
              <div style={styles.spinner} />
              <p>Processing your payment...</p>
            </div>
          )}

          {/* PayPal Button or Setup Message */}
          {config?.paypalConnected ? (
            <div>
              <p style={styles.secureNote}>
                🔒 Secure payment powered by PayPal
              </p>
              <div id="paypal-button-container" style={styles.paypalContainer} />
            </div>
          ) : (
            <div style={styles.notConfigured}>
              <div style={styles.notConfiguredIcon}>⚙️</div>
              <p style={styles.notConfiguredText}>
                Payment system is being configured. Please check back shortly or contact
                Executive Springboard directly.
              </p>
              <a
                href="https://calendly.com/smoss-1/30min"
                target="_blank"
                rel="noreferrer"
                style={styles.contactLink}
              >
                Schedule a consultation instead →
              </a>
            </div>
          )}

          <p style={styles.privacy}>
            Your payment is secured by PayPal. We never store your card details.
          </p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: { minHeight: '100vh', backgroundColor: 'var(--color-off-white)', fontFamily: 'var(--font-primary)' },
  loadingPage: { minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px' },
  spinner: { width: '36px', height: '36px', border: '4px solid var(--color-light-gray)', borderTopColor: 'var(--color-teal)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  loadingText: { fontSize: '15px', color: 'var(--color-text-light)' },
  header: { backgroundColor: 'var(--color-primary)', padding: '0 24px', height: '64px', display: 'flex', alignItems: 'center' },
  logo: { display: 'flex', flexDirection: 'column', gap: '1px' },
  logoExec: { fontSize: '9px', fontWeight: '700', letterSpacing: '3px', color: 'var(--color-teal)', lineHeight: 1 },
  logoSpring: { fontSize: '18px', fontWeight: '700', color: '#FFFFFF', lineHeight: 1 },
  container: { maxWidth: '520px', margin: '0 auto', padding: '40px 24px' },
  progressWrap: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px' },
  progressBar: { flex: 1, height: '6px', backgroundColor: 'var(--color-light-gray)', borderRadius: '100px', overflow: 'hidden' },
  progressFill: { height: '100%', backgroundColor: 'var(--color-teal)', borderRadius: '100px' },
  progressText: { fontSize: '13px', color: 'var(--color-text-light)', whiteSpace: 'nowrap' },
  card: { backgroundColor: '#fff', borderRadius: '16px', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-md)', padding: '40px' },
  title: { fontSize: '26px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '8px' },
  subtitle: { fontSize: '15px', color: 'var(--color-text-light)', marginBottom: '28px' },
  priceBox: { backgroundColor: 'var(--color-off-white)', borderRadius: '12px', padding: '24px', textAlign: 'center', marginBottom: '20px', border: '1px solid var(--color-border)' },
  priceLabel: { fontSize: '14px', color: 'var(--color-text-light)', marginBottom: '8px' },
  priceAmount: { fontSize: '48px', fontWeight: '700', color: 'var(--color-primary)', lineHeight: 1, marginBottom: '8px' },
  priceNote: { fontSize: '13px', color: 'var(--color-text-light)' },
  includesBox: { backgroundColor: 'var(--color-info-bg)', borderRadius: '10px', padding: '16px 20px', marginBottom: '24px' },
  includesTitle: { fontSize: '13px', fontWeight: '600', color: 'var(--color-teal-dark)', marginBottom: '10px' },
  includeItem: { display: 'flex', gap: '10px', fontSize: '13px', color: 'var(--color-text)', marginBottom: '6px', alignItems: 'flex-start' },
  includeCheck: { color: 'var(--color-teal)', fontWeight: '700', flexShrink: 0 },
  errorBox: { background: 'var(--color-error-bg)', color: 'var(--color-error)', border: '1px solid #F5C6CB', borderRadius: '8px', padding: '12px 16px', fontSize: '14px', marginBottom: '16px' },
  payingBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '20px', backgroundColor: 'var(--color-off-white)', borderRadius: '10px', marginBottom: '16px' },
  secureNote: { fontSize: '13px', color: 'var(--color-text-light)', textAlign: 'center', marginBottom: '12px' },
  paypalContainer: { minHeight: '50px' },
  notConfigured: { textAlign: 'center', padding: '32px 20px', border: '2px dashed var(--color-border)', borderRadius: '12px', marginBottom: '16px' },
  notConfiguredIcon: { fontSize: '36px', marginBottom: '12px' },
  notConfiguredText: { fontSize: '14px', color: 'var(--color-text-light)', lineHeight: '1.6', marginBottom: '16px' },
  contactLink: { color: 'var(--color-teal)', fontSize: '14px', fontWeight: '500' },
  privacy: { fontSize: '12px', color: 'var(--color-text-light)', textAlign: 'center', marginTop: '16px' },
};

export default PaymentPage;
